import ContentCard from '@/components/ContentCard';
import CourseCard from '@/components/CourseCard';
import { HomeSectionContent } from '@/components/HomeSectionContent';
import LMSCourseCard from '@/components/LMSCourseCard';
import '@/components/Skeleton/styles.scss';
import Constants from '@/Constants';
import { ViewType } from '@/models/ViewType';
import { contentService } from '@/services/ContentService';
import { courseService } from '@/services/CourseService';
import { lmsCourseService } from '@/services/LMSCourseService';
import { ApplicationState } from '@/store';
import { ContentStudentView } from '@/store/item/types';
import { CourseStudentView } from '@/store/list/types';
import React, { useCallback, useMemo } from 'react';
import MagicSliderDots from 'react-magic-slider-dots';
import 'react-magic-slider-dots/dist/magic-dots.css';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import _ from 'underscore';
import './styles.scss';

const isLmsCardDisabled = (status: string, isExpired: boolean): boolean => !!(isExpired && !lmsCourseService.isCourseCompletedPassedOrFailed(status));
const SLIDES_TO_SHOW = 4;
const SLIDES_TO_SCROLL = 4;
const CONTENT_LIMIT = 2;

interface HomeStudentViewSectionProps {
    viewType: ViewType;
    title?: string;
    seeAllPath: string;
    onLoadMoreSlideContent: () => void;
}

const HomeStudentViewSection: React.FC<HomeStudentViewSectionProps> = ({ viewType, onLoadMoreSlideContent, title = '', seeAllPath }) => {
    const { coursesListData, contentListData } = useSelector((state: ApplicationState) => ({
        modules: state.appOrganization.data.modules,
        person: state.identity.data.person,
        coursesListData: state.lists.listData[viewType],
        contentListData: state.itens.listData[viewType],
    }));

    const renderCourseCard = ({ course, courseEnrollment, programClass, classEnrollment }: CourseStudentView) => {
        const lmsCardDisabled = isLmsCardDisabled(courseEnrollment?.status, programClass.isExpired);

        const linkTo =
            course.moduleId === Constants.PLAYLIST_MODULE_ID
                ? `${Constants.COURSES_RESOURCE_NAME}/${course.id}`
                : course.moduleId === Constants.LMS_MODULE_ID
                ? `/lms/enrollments/${classEnrollment.id}/courses/${course.id}`
                : 'not-defined';

        return (
            <Link className="link-to-course" key={_.uniqueId('course_card_')} to={linkTo}>
                {course.moduleId === Constants.PLAYLIST_MODULE_ID ? (
                    <CourseCard course={course} courseEnrollment={courseEnrollment} mode="view" totalRating={courseService.getTotalRating(course)} />
                ) : (
                    course.moduleId === Constants.LMS_MODULE_ID && (
                        <LMSCourseCard
                            course={course}
                            courseEnrollment={courseEnrollment}
                            disabled={lmsCardDisabled}
                            mode="view"
                            programClass={programClass}
                        />
                    )
                )}
            </Link>
        );
    };

    const renderContentCard = ({ content, contentEnrollment }: ContentStudentView) => {
        return (
            <Link className="link-to-content" key={`home-content-card-${content.id}`} to={`/${Constants.CONTENTS_RESOURCE_NAME}/${content.id}`}>
                <ContentCard content={content} finished={contentService.getContentStatusIsCompleted(contentEnrollment)} />
            </Link>
        );
    };

    const handleLoadMoreOnAfterChange = useCallback(
        (currentSlide: number) => {
            if (coursesListData.total <= SLIDES_TO_SCROLL || currentSlide === 0 || currentSlide < coursesListData.list.length - SLIDES_TO_SCROLL) {
                return;
            }

            const slideToLoadMore: number = coursesListData.list.length / 2;
            if (coursesListData.list.length > SLIDES_TO_SCROLL && currentSlide >= slideToLoadMore) {
                if (coursesListData.list.length < coursesListData.total) {
                    onLoadMoreSlideContent();
                }
            }
        },
        [coursesListData.total, onLoadMoreSlideContent],
    );

    const renderDots = useCallback((dots: boolean) => <MagicSliderDots dotWidth={30} dots={dots} numDotsToShow={4} />, [coursesListData]);

    const renderCoursesCardSkeleton = () => {
        return (
            <div className="skeleton-courses-container skeleton-container skeleton-wave">
                {[...new Array(SLIDES_TO_SHOW)].map((_, index) => (
                    <div className="skeleton-item" key={`skeleton-course-item-${index}`} />
                ))}
            </div>
        );
    };

    const renderContentCardSkeleton = () => {
        return (
            <div className="skeleton-content-container skeleton-container skeleton-wave">
                {[...new Array(CONTENT_LIMIT)].map((_, index) => (
                    <div className="skeleton-item" key={`skeleton-content-item-${index}`} />
                ))}
            </div>
        );
    };

    const contentList = useMemo(() => {
        if (!contentListData) {
            return [];
        }
        const { list } = contentListData;
        return list.length > CONTENT_LIMIT ? contentListData.list.splice(0, CONTENT_LIMIT) : list;
    }, [contentListData]);

    const sliderSettings = useMemo<Settings>(
        () => ({
            dots: true,
            infinite: false,
            draggable: false,
            centerMode: false,
            appendDots: renderDots,
            slidesToShow: SLIDES_TO_SHOW,
            slidesToScroll: SLIDES_TO_SCROLL,
            afterChange: handleLoadMoreOnAfterChange,
        }),
        [handleLoadMoreOnAfterChange, renderDots],
    );

    const seeAllPathHandlered = useMemo(() => {
        return contentListData.total > CONTENT_LIMIT ? seeAllPath : undefined;
    }, [contentListData.total, seeAllPath]);

    const canNotShowSection = useMemo(() => {
        return !coursesListData.loading && !contentListData.loading && coursesListData.list.length === 0 && contentListData.list.length === 0;
    }, [coursesListData, contentListData]);

    if (canNotShowSection) {
        return null;
    }

    return (
        <HomeSectionContent seeAllPath={seeAllPathHandlered} title={title}>
            <div className="home-student-view-section-container">
                {coursesListData.loading ? (
                    renderCoursesCardSkeleton()
                ) : (
                    <Slider {...sliderSettings}>{coursesListData.list.map(renderCourseCard)}</Slider>
                )}

                <div className="card-content-container">
                    {contentListData.loading ? renderContentCardSkeleton() : contentList.map(renderContentCard)}
                </div>
            </div>
        </HomeSectionContent>
    );
};

export { HomeStudentViewSection };
